import React from 'react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameDay, isWithinInterval, startOfDay, isSameMonth } from 'date-fns';
import { nb } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Booking } from '@/types/booking';

interface VisualMonthCalendarProps {
  currentMonth: Date;
  selectedDate?: Date;
  bookings: Booking[];
  onDateSelect: (date: Date) => void;
  onMonthChange: (date: Date) => void;
}

const VisualMonthCalendar: React.FC<VisualMonthCalendarProps> = ({
  currentMonth,
  selectedDate,
  bookings,
  onDateSelect,
  onMonthChange
}) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const dateFormat = "d";
  const rows = [];

  let days = [];
  let day = startDate;
  let formattedDate = "";

  // Get bookings for a specific day
  const getBookingsForDay = (day: Date) => {
    return bookings.filter(booking => 
      isWithinInterval(startOfDay(day), {
        start: startOfDay(booking.startDate),
        end: startOfDay(booking.endDate)
      })
    );
  };

  // Create calendar grid
  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      formattedDate = format(day, dateFormat);
      const cloneDay = day;
      const dayBookings = getBookingsForDay(day);
      const isCurrentMonth = isSameMonth(day, monthStart);
      const isToday = isSameDay(day, new Date());
      const isSelected = selectedDate && isSameDay(day, selectedDate);

      days.push(
        <div
          className={`min-h-[80px] border border-gray-200 p-2 cursor-pointer transition-colors relative ${
            !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white hover:bg-gray-50'
          } ${isSelected ? 'bg-blue-50 border-blue-300' : ''} ${isToday ? 'bg-yellow-50' : ''}`}
          key={day.toString()}
          onClick={() => onDateSelect(cloneDay)}
        >
          <div className={`text-sm mb-1 ${isToday ? 'font-bold text-blue-600' : ''}`}>
            {formattedDate}
          </div>
          
          {/* Booking indicators */}
          {dayBookings.length > 0 && (
            <div className="space-y-1">
              {dayBookings.slice(0, 2).map((booking, index) => (
                <div
                  key={booking.id}
                  className="text-xs px-1 py-0.5 rounded bg-green-500 text-white truncate"
                  title={`${booking.companyName} - ${booking.articleTitle || 'Ingen tittel'}`}
                >
                  {booking.companyName}
                </div>
              ))}
              {dayBookings.length > 2 && (
                <div className="text-xs px-1 py-0.5 rounded bg-blue-500 text-white">
                  +{dayBookings.length - 2} flere
                </div>
              )}
            </div>
          )}

          {/* Visual booking coverage bar */}
          {dayBookings.length > 0 && (
            <div className="absolute bottom-1 left-1 right-1 h-1 bg-green-500 rounded opacity-60" />
          )}
        </div>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <div className="grid grid-cols-7" key={day.toString()}>
        {days}
      </div>
    );
    days = [];
  }

  const previousMonth = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    onMonthChange(prevMonth);
  };

  const nextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    onMonthChange(nextMonth);
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy', { locale: nb })}
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={previousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onMonthChange(new Date())}>
            I dag
          </Button>
          <Button variant="outline" size="sm" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 border-b">
        {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(day => (
          <div key={day} className="p-3 text-center text-sm font-medium text-gray-600 border-r last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-0">
        {rows}
      </div>

      {/* Legend */}
      <div className="p-4 border-t bg-gray-50">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-2 bg-green-500 rounded" />
            <span>Booking periode</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-50 border border-blue-300 rounded" />
            <span>Valgt dato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded" />
            <span>I dag</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualMonthCalendar;